import { useEffect, useMemo, useState } from "react";
import {
  Receipt,
  Search,
  CheckCircle2,
  AlertTriangle,
  Clock3,
  TrendingUp,
  CalendarDays,
} from "lucide-react";
import { api } from "../../lib/auth";

function StatusPill({ status }) {
  const styles = {
    paid: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border border-amber-200",
    overdue: "bg-rose-50 text-rose-700 border border-rose-200",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold capitalize ${
        styles[status] || "bg-slate-100 text-slate-700 border border-slate-200"
      }`}
    >
      {status}
    </span>
  );
}

function StatCard({
  title,
  value,
  icon: Icon,
  tint = "blue",
  subtitle,
}) {
  const styles = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <h3 className="mt-3 text-3xl font-black tracking-tight text-slate-900">
            {value}
          </h3>

          {subtitle && (
            <p className="mt-2 text-xs text-slate-500">{subtitle}</p>
          )}
        </div>

        <div
          className={`flex h-12 w-12 items-center justify-center rounded-2xl ${styles[tint]}`}
        >
          <Icon size={22} />
        </div>
      </div>
    </div>
  );
}

export default function AdminInvoices() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/invoices").then((r) => setRows(r.data || []));
  }, []);

  const filteredRows = useMemo(() => {
    return rows.filter((i) => {
      const matchesSearch =
        (i.invoice_number || "")
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        (i.tenant || "")
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        (i.plan || "")
          .toLowerCase()
          .includes(search.toLowerCase());

      const matchesFilter =
        filter === "all" ? true : i.status === filter;

      return matchesSearch && matchesFilter;
    });
  }, [rows, search, filter]);

  // Stats
  const total = rows.length;
  const paid = rows.filter((i) => i.status === "paid").length;
  const overdue = rows.filter((i) => i.status === "overdue").length;
  const revenue = rows.reduce(
    (sum, i) => sum + Number(i.amount || 0),
    0
  );

  return (
    <div className="min-h-screen bg-[#F7F9FC] p-6 lg:p-10">
      <div className="mx-auto max-w-[1600px]">

        {/* Header */}
        <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700">
              <Receipt size={16} />
              Billing & Revenue
            </div>

            <h1 className="mt-4 text-4xl font-black tracking-tight text-slate-900">
              Invoice Management
            </h1>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              Track invoice payments, tenant billing records, overdue balances,
              and total platform revenue in one clean dashboard.
            </p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                <TrendingUp size={22} />
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Total Revenue
                </p>

                <h2 className="text-2xl font-black text-slate-900">
                  ₦{revenue.toLocaleString()}
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            title="Total Invoices"
            value={total.toLocaleString()}
            icon={Receipt}
            tint="blue"
            subtitle="All invoices generated"
          />

          <StatCard
            title="Paid Invoices"
            value={paid.toLocaleString()}
            icon={CheckCircle2}
            tint="emerald"
            subtitle="Successfully paid invoices"
          />

          <StatCard
            title="Pending"
            value={rows
              .filter((i) => i.status === "pending")
              .length.toLocaleString()}
            icon={Clock3}
            tint="amber"
            subtitle="Awaiting payment"
          />

          <StatCard
            title="Overdue"
            value={overdue.toLocaleString()}
            icon={AlertTriangle}
            tint="rose"
            subtitle="Requires immediate attention"
          />
        </div>

        {/* Main Table */}
        <div className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm">

          {/* Top Bar */}
          <div className="flex flex-col gap-4 border-b border-slate-200 p-6 lg:flex-row lg:items-center lg:justify-between">

            <div>
              <h2 className="text-xl font-bold text-slate-900">
                All Invoices
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                {filteredRows.length} invoice records found
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">

              {/* Search */}
              <div className="relative">
                <Search
                  size={17}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search invoice, tenant, or plan..."
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm outline-none transition focus:border-blue-500 focus:bg-white sm:w-[300px]"
                />
              </div>

              {/* Filter */}
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="h-12 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-medium outline-none focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200">
                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Invoice
                  </th>

                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Tenant
                  </th>

                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Plan
                  </th>

                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Amount
                  </th>

                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Status
                  </th>

                  <th className="px-6 py-4 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Date Issued
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {filteredRows.map((i) => (
                  <tr
                    key={i.id}
                    className="transition hover:bg-slate-50/80"
                  >
                    <td className="px-6 py-5">
                      <div className="font-mono text-sm font-semibold text-slate-900">
                        {i.invoice_number}
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <div className="font-semibold text-slate-900">
                        {i.tenant}
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <div className="text-sm text-slate-600">
                        {i.plan}
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <div className="font-bold text-slate-900">
                        ₦{Number(i.amount || 0).toLocaleString()}
                      </div>
                    </td>

                    <td className="px-6 py-5">
                      <StatusPill status={i.status} />
                    </td>

                    <td className="px-6 py-5">
                      <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                        <CalendarDays size={14} />
                        {(i.date || "").slice(0, 10)}
                      </div>
                    </td>
                  </tr>
                ))}

                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-20 text-center">
                      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-100">
                        <Receipt size={32} className="text-slate-400" />
                      </div>

                      <h3 className="mt-5 text-lg font-bold text-slate-900">
                        No invoices found
                      </h3>

                      <p className="mt-2 text-sm text-slate-500">
                        There are currently no invoice records available.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}